// Normalizer takes arbitrary legacy/V2+ shapes and emits the V2+ wire format.
// `any` is intentional at the boundary -- shapes vary by builder mode.

import {
    ensureRagStepTree, ensureRagStepsInOpcode
} from 'builders/builderOpcode/jsonMode/ragStepWireDefaults';

/**
 * A-0008.03 PR-3 Stage 3 — V2+ wire normalizer.
 *
 * The legacy V2 OpCode builder UI carries state in the OLD shape:
 *   { name, inputs: string[], output: { name: oel }, _id, created, ... }
 *
 * The new Server Joi (PR-1) REJECTS that shape. This normalizer runs in the
 * service layer (jsonOpcode.service.ts) on every create/update, converting
 * legacy fields to V2+ shape so existing UI components keep working without
 * a deeper rebuild.
 *
 * Operations applied (per OPCODE-SCHEMA.md §16 + V2+ Joi rules):
 *   - drop top-level `_id`, `id`, flat audit fields (`created`/`createdBy`/...)
 *   - rename `name` -> `title` (canonical V2+ display name)
 *   - convert `inputs: string[]` -> `inputs: TypedInput[]`
 *     (each: {name, type:"string", required:true, description:"", default:null})
 *   - convert `output: { name: oel }` -> `outputs: TypedOutput[]`
 *     (each: {name, type:"string", description:"", oel_expression:oel})
 *     -- nested maps flatten to dotted names
 *   - drop step `config:` wrapper if present (kind-specific fields go flat)
 *   - rename api `inputs.timeout` -> `inputs.request_timeout_s`
 *   - reject `kind:"wait"` steps (server would 400 anyway; do it client-side
 *     for a better error)
 *
 * Idempotent: docs already in V2+ shape pass through unchanged.
 *
 * NOT populated here (server-managed):
 *   - opcode_id (server generates on create)
 *   - audit.* (server populates created/createdBy on POST, updated/updatedBy on PUT)
 *   - resources_used (server populateAutoFields after Joi validation)
 *   - engine_capabilities_required.requires[] (same)
 */

function isV2PlusOpcode(o: any): boolean {
    if (!o || typeof o !== 'object') return false;
    return (
        '_id' in o === false &&
        'id' in o === false &&
        'name' in o === false &&
        'output' in o === false &&
        'created' in o === false &&
        'createdBy' in o === false &&
        Array.isArray(o.outputs) &&
        Array.isArray(o.inputs) &&
        (o.inputs.length === 0 || typeof o.inputs[0] === 'object')
    );
}

function normalizeInputs(legacy: any): any[] {
    if (!Array.isArray(legacy)) return [];
    return legacy.map((entry) => {
        if (typeof entry === 'string') {
            return {
                name: entry,
                type: 'string',
                required: true,
                description: '',
                default: null,
            };
        }
        if (entry && typeof entry === 'object' && entry.name) {
            // already TypedInput-shaped
            return {
                name: entry.name,
                type: entry.type || 'string',
                required: entry.required !== false,
                description: entry.description || '',
                default: entry.default ?? null,
                ...(entry.constraint ? {
 constraint: entry.constraint
} : {}),
                ...(entry.examples ? {
 examples: entry.examples
} : {}),
                ...(entry.sensitive !== undefined ? {
 sensitive: entry.sensitive
} : {}),
            };
        }
        return null;
    }).filter((x: any) => x !== null);
}

function flattenOutputMap(map: Record<string, any>, prefix = ''): Array<{ name: string; oel: string }> {
    const out: Array<{ name: string; oel: string }> = [];
    if (!map || typeof map !== 'object') return out;
    for (const [k, v] of Object.entries(map)) {
        const path = prefix ? `${prefix}.${k}` : k;
        if (typeof v === 'string') {
            out.push({
 name: path,
oel: v
});
        } else if (Array.isArray(v)) {
            out.push({
 name: path,
oel: JSON.stringify(v)
});
        } else if (v && typeof v === 'object') {
            out.push(...flattenOutputMap(v, path));
        }
    }
    return out;
}

function normalizeOutputs(legacyOutputs: any, legacyOutputMap: any): any[] {
    // Prefer V2+ outputs[] if present.
    if (Array.isArray(legacyOutputs)) {
        return legacyOutputs.map((entry: any) => {
            if (entry && typeof entry === 'object' && entry.name && entry.oel_expression) {
                return {
                    name: entry.name,
                    type: entry.type || 'string',
                    description: entry.description || '',
                    oel_expression: entry.oel_expression,
                    ...(entry.schema ? {
 schema: entry.schema
} : {}),
                };
            }
            return null;
        }).filter((x: any) => x !== null);
    }
    // Fall back to legacy output map.
    if (legacyOutputMap && typeof legacyOutputMap === 'object') {
        return flattenOutputMap(legacyOutputMap).map((p) => ({
            name: p.name,
            type: 'string',
            description: '',
            oel_expression: p.oel,
        }));
    }
    return [];
}

function normalizeStep(step: any): any {
    if (!step || typeof step !== 'object') return step;

    // If step has `config:` wrapper (legacy/internal), spread it onto the step.
    let merged = {
 ...step
};
    if (merged.config && typeof merged.config === 'object') {
        merged = {
 ...merged.config,
...merged
};
        delete merged.config;
    }

    // Drop unknown keys NOT in the step common set or kind-specific allow list.
    // (Be permissive -- pass through anything that looks legitimate.)

    // Rename api inputs.timeout -> inputs.request_timeout_s.
    if (merged.kind === 'api' && merged.inputs && typeof merged.inputs === 'object'
        && 'timeout' in merged.inputs && !('request_timeout_s' in merged.inputs)) {
        merged.inputs = {
            ...merged.inputs,
            request_timeout_s: merged.inputs.timeout,
        };
        delete merged.inputs.timeout;
    } else if (merged.kind === 'api' && merged.inputs && 'timeout' in merged.inputs) {
        // Both present -- prefer request_timeout_s, drop timeout.
        const inputs = {
 ...merged.inputs
};
        delete inputs.timeout;
        merged.inputs = inputs;
    }

    // Recurse into parallel branches.
    if (merged.kind === 'parallel' && Array.isArray(merged.branches)) {
        merged.branches = merged.branches.map(normalizeStep);
    }

    return ensureRagStepTree(merged);
}

function normalizeFlows(flows: any[]): any[] {
    if (!Array.isArray(flows)) return [];
    return flows.map((flow: any) => {
        if (!flow || typeof flow !== 'object') return flow;
        return {
            id: flow.id,
            steps: Array.isArray(flow.steps) ? flow.steps.map(normalizeStep) : [],
            output: Array.isArray(flow.output)
                ? flow.output // already TypedOutput[]
                : flattenOutputMap(flow.output || {}).map((p) => ({
                    name: p.name,
                    type: 'string',
                    description: '',
                    oel_expression: p.oel,
                })),
        };
    });
}

/**
 * Reject any step whose kind is `wait` (Server Joi rejects, but client-side
 * gives a better error). Returns the array filtered + a list of rejected ids.
 */
function rejectWaitSteps(steps: any[]): { kept: any[]; rejected: string[] } {
    const rejected: string[] = [];
    const kept: any[] = [];
    for (const s of steps || []) {
        if (s && s.kind === 'wait') {
            rejected.push(s.id || '<unknown>');
            continue;
        }
        kept.push(s);
    }
    return {
 kept,
rejected
};
}

export class V2PlusNormalizationError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'V2PlusNormalizationError';
    }
}

/**
 * Convert any legacy/V2+ opcode shape to the V2+ wire format expected by
 * the Server Joi (PR-1). Idempotent. Returns a NEW object (does not mutate input).
 */
export function normalizeToV2PlusWire(opcode: any): any {
    if (!opcode || typeof opcode !== 'object') {
        throw new V2PlusNormalizationError('opcode must be an object');
    }

    const out = isV2PlusOpcode(opcode) ? { ...opcode } : normalizeLegacyOpcode(opcode);
    return ensureRagStepsInOpcode(out);
}

function normalizeLegacyOpcode(opcode: any): any {
    const out: any = {};

    // Schema discriminator + content version
    out.schema_version = opcode.schema_version || '2.0';
    if (opcode.version) out.version = opcode.version;

    // Identity: title (was `name`), description, optional categorization.
    // opcode_id is server-generated on create; on update, the path opcodeId
    // is canonical. We pass through if present (server will validate match).
    if (opcode.opcode_id) out.opcode_id = opcode.opcode_id;
    out.title = opcode.title || opcode.name || '';
    if (opcode.description !== undefined) out.description = opcode.description;
    if (Array.isArray(opcode.tags)) out.tags = opcode.tags;
    if (opcode.category !== undefined) out.category = opcode.category;

    // Governance hints (Future-slot-V1, informational)
    if (opcode.governance_hints) out.governance_hints = opcode.governance_hints;

    // Execution defaults (timeout cascade source)
    if (typeof opcode.default_step_timeout_ms === 'number') {
        out.default_step_timeout_ms = opcode.default_step_timeout_ms;
    }

    // Typed interface
    out.inputs = normalizeInputs(opcode.inputs);
    out.outputs = normalizeOutputs(opcode.outputs, opcode.output);

    // Procedure
    const stepsRaw = Array.isArray(opcode.steps) ? opcode.steps : [];
    const {
 kept: keptSteps, rejected: rejectedSteps
} = rejectWaitSteps(stepsRaw);
    if (rejectedSteps.length > 0) {
        throw new V2PlusNormalizationError(
            'wait-step kind is no longer supported in V1 (A-0008.03). '
            + `Hand-migrate or remove these step ids before saving: ${rejectedSteps.join(', ')}`,
        );
    }
    out.steps = keptSteps.map(normalizeStep);

    if (Array.isArray(opcode.definitions)) {
        const {
 kept, rejected
} = rejectWaitSteps(opcode.definitions);
        if (rejected.length > 0) {
            throw new V2PlusNormalizationError(
                `wait-step kind not supported in definitions: ${rejected.join(', ')}`,
            );
        }
        out.definitions = kept.map(normalizeStep);
    }
    if (Array.isArray(opcode.flows)) {
        out.flows = normalizeFlows(opcode.flows);
    }

    // Hooks slot (Future-slot-V1)
    if (opcode.hooks) out.hooks = opcode.hooks;

    // Audit -- nest if flat fields are present, otherwise pass through if nested.
    if (opcode.audit && typeof opcode.audit === 'object') {
        out.audit = opcode.audit;
    } else if (opcode.created || opcode.createdBy || opcode.updated || opcode.updatedBy) {
        const audit: any = {};
        if (opcode.created) audit.created = opcode.created;
        if (opcode.createdBy) audit.createdBy = opcode.createdBy;
        if (opcode.updated) audit.updated = opcode.updated;
        if (opcode.updatedBy) audit.updatedBy = opcode.updatedBy;
        out.audit = audit;
    }

    // resources_used / engine_capabilities_required: Server's populateAutoFields
    // re-derives these on save -- pass through if present, else server fills.
    if (opcode.resources_used) out.resources_used = opcode.resources_used;
    if (opcode.engine_capabilities_required) out.engine_capabilities_required = opcode.engine_capabilities_required;

    // Legacy fields explicitly DROPPED:
    //   _id, id, name, output, created, createdBy, updated, updatedBy
    // (Already excluded by not copying them.)

    return out;
}
